import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import simplifiedAuthService from '../../services/simplifiedAuthService';
import firestoreOTPService from '../../services/firestoreOTPService';
import sessionManager from '../../services/sessionManager';

const initialState = {
  isAuthenticated: false,
  isAuthReady: false,
  isAuthorized: false,
  isProfileLoaded: false,
  user: null,
  profile: null,
  organization: null,
  role: null,
  permissions: {},
  error: null,
  isLoading: false,
  // Session management state
  sessionId: null,
  sessionExpiresAt: null,
  lastActivity: null,
  isSessionRestored: false,
  // Loading phase: 'google' | 'otp' | null
  authLoadingPhase: null,
  // OTP verification state
  otpVerificationRequired: false,
  otpVerificationCompleted: false,
  otpError: null,
  // Temporary access state
  tempAccess: {
    token: null,
    isValid: false,
    lastValidated: null,
    isMonitoring: false
  }
};

// Async thunks for simplified auth
export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const result = await simplifiedAuthService.signInWithGoogle();
      if (result.success) {
        if (result.redirecting) {
          return result;
        }
        if (result.user?.email) {
          // Show OTP loading phase while we send the code
          dispatch({ type: 'auth/setAuthLoadingPhase', payload: 'otp' });
          await firestoreOTPService.requestOTP(result.user.email);
        }
        return result;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const requestOtp = createAsyncThunk(
  'auth/requestOtp',
  async (email, { rejectWithValue }) => {
    try {
      const result = await firestoreOTPService.requestOTP(email);
      if (result.success) {
        return result;
      } else {
        return rejectWithValue(result.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      const result = await simplifiedAuthService.signOut();
      if (result.success) {
        return result;
      } else {
        return rejectWithValue(result.error);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// OTP verification async thunks - generateOTP is now removed
export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ user, otpCode }, { rejectWithValue }) => {
    try {
      const result = await firestoreOTPService.verifyOTP(user.email, otpCode);
      if (result.success) {
        // OTP is valid, now create session and load permissions.
        const sessionResult = await sessionManager.createSession(user.email, user);
        if (!sessionResult.success) {
          return rejectWithValue('Failed to create session.');
        }

        const permissions = await simplifiedAuthService.loadUserPermissionsAfterOTP(user.uid, user.email);
        
        return {
          user,
          permissions,
          session: {
            id: sessionResult.sessionId,
            expiresAt: sessionResult.expiresAt,
          },
          organization: { id: 'drishti_lifesaving_org_id', name: 'Drishti Lifesaving' },
          role: 'admin', // Placeholder, should be derived from permissions
        };
      } else {
        return rejectWithValue(result.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyOtpAndLogin = createAsyncThunk(
  'auth/verifyOtpAndLogin',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      // This single call should handle OTP verification, user creation/retrieval,
      // and session creation on the backend.
      const result = await firestoreOTPService.verifyOtpAndLogin(email, otp);

      if (result.success) {
        // The backend should return a comprehensive payload.
        const { user, session, permissions, organization, role } = result;

        // Start client-side session tracking
        sessionManager.startSession(session.id, session.expiresAt, user);
        if (permissions) {
          sessionManager.savePermissions(permissions);
        }
        sessionManager.initActivityTracking();

        return {
          user,
          session,
          permissions,
          organization,
          role,
        };
      } else {
        return rejectWithValue(result.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Session restoration async thunk
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Attempting to restore session from localStorage...');

      // Validate stored session with SessionManager
      const validationResult = await sessionManager.validateStoredSession();

      if (validationResult.isValid) {
        console.log('✅ Session restored successfully');

        // Load user permissions after session restoration
        const userData = validationResult.sessionData.userData;
        let loadedPermissions = null;

        try {
          loadedPermissions = await simplifiedAuthService.loadUserPermissionsAfterOTP(
            userData.uid,
            userData.email
          );
        } catch (permError) {
          console.warn('Could not load permissions from server, checking cache:', permError);
        }

        if (!loadedPermissions) {
          loadedPermissions = validationResult.sessionData.cachedPermissions || sessionManager.getCachedPermissions();
          if (loadedPermissions) {
            console.log('Using cached permissions for session restore');
          }
        } else {
          sessionManager.savePermissions(loadedPermissions);
        }

        return {
          sessionId: validationResult.sessionData.sessionId,
          sessionExpiresAt: validationResult.sessionData.sessionExpiresAt,
          lastActivity: validationResult.sessionData.lastActivity,
          user: userData,
          permissions: loadedPermissions,
          organization: {
            id: 'drishti_lifesaving_org_id',
            name: 'Drishti Lifesaving'
          },
          role: userData.role || 'admin',
          warning: validationResult.warning // Backend unavailable warning
        };
      } else {
        console.log('❌ Session restoration failed:', validationResult.reason);
        return rejectWithValue(validationResult.reason);
      }
    } catch (error) {
      console.error('❌ Error during session restoration:', error);
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthReady: (state) => {
      state.isAuthReady = true;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    // OTP verification reducers
    setOTPVerificationRequired: (state, action) => {
      state.otpVerificationRequired = action.payload;
    },
    setOTPVerificationCompleted: (state, action) => {
      state.otpVerificationCompleted = action.payload;
    },
    setOTPError: (state, action) => {
      state.otpError = action.payload;
    },
    clearOTPError: (state) => {
      state.otpError = null;
    },
    // Temporary access reducers
    setTempAccessToken: (state, action) => {
      state.tempAccess.token = action.payload;
    },
    updateTempAccessStatus: (state, action) => {
      state.tempAccess.isValid = action.payload.isValid;
      state.tempAccess.lastValidated = action.payload.lastValidated;
    },
    setTempAccessMonitoring: (state, action) => {
      state.tempAccess.isMonitoring = action.payload;
    },
    clearTempAccess: (state) => {
      state.tempAccess = {
        token: null,
        isValid: false,
        lastValidated: null,
        isMonitoring: false
      };
    },
    loginSuccess: (state, action) => {
      // This is a generic login success handler for temp access
      state.isAuthenticated = true;
      state.isAuthorized = true;
      state.isProfileLoaded = true;
      if (action.payload.user) {
        state.user = action.payload.user;
      }
      if (action.payload.permissions) {
        state.permissions = action.payload.permissions;
      }
      if (action.payload.organization) {
        state.organization = action.payload.organization;
      }
      if (action.payload.role) {
        state.role = action.payload.role;
      }
      // Store session data if provided
      if (action.payload.sessionId) {
        state.sessionId = action.payload.sessionId;
      }
      if (action.payload.sessionExpiresAt) {
        state.sessionExpiresAt = action.payload.sessionExpiresAt;
      }
      if (action.payload.lastActivity) {
        state.lastActivity = action.payload.lastActivity;
      }
    },
    setAuthLoadingPhase: (state, action) => {
      state.authLoadingPhase = action.payload;
    },
    // Session management reducers
    setSessionData: (state, action) => {
      state.sessionId = action.payload.sessionId;
      state.sessionExpiresAt = action.payload.sessionExpiresAt;
      state.lastActivity = action.payload.lastActivity;
    },
    updateLastActivity: (state, action) => {
      state.lastActivity = action.payload;
    },
    clearSession: (state) => {
      state.sessionId = null;
      state.sessionExpiresAt = null;
      state.lastActivity = null;
      state.isSessionRestored = false;
    },
    setSessionRestored: (state, action) => {
      state.isSessionRestored = action.payload;
    },
    // The onAuthStateChanged logic is now handled by the async thunks
    // onAuthStateChanged: (state, action) => { ... }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.authLoadingPhase = 'google';
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.authLoadingPhase = null;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.otpVerificationRequired = true; // Trigger OTP screen
        state.error = null;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.authLoadingPhase = null;
        state.isAuthenticated = false;
        state.user = null;
        state.permissions = {};
        state.isAuthorized = false;
        state.isProfileLoaded = false;
        state.error = action.payload;
      })
      .addCase(signOut.fulfilled, (state) => {
        // Reset the entire auth state to initial
        Object.assign(state, initialState);
        // Explicitly clear session from localStorage to prevent race conditions
        sessionManager.clearSession();
      })
      // OTP verification cases
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.otpError = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpVerificationCompleted = true;
        state.otpVerificationRequired = false;
        state.otpError = null;

        // Set all auth state at once upon successful verification
        state.isAuthenticated = true;
        state.isAuthorized = true;
        state.isProfileLoaded = true;
        state.user = action.payload.user;
        state.profile = action.payload.user;
        state.permissions = action.payload.permissions;
        state.organization = action.payload.organization;
        state.role = action.payload.role;
        state.sessionId = action.payload.session.id;
        state.sessionExpiresAt = action.payload.session.expiresAt;
        state.lastActivity = new Date().toISOString();
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.otpError = action.payload;
        console.log('OTP verification failed:', action.payload);
      })
      // New cases for the unified login flow
      .addCase(verifyOtpAndLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.otpError = null;
      })
      .addCase(verifyOtpAndLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.isAuthorized = true;
        state.isProfileLoaded = true;
        state.otpVerificationCompleted = true;
        state.otpVerificationRequired = false;
        state.user = action.payload.user;
        state.profile = action.payload.user;
        state.permissions = action.payload.permissions;
        state.organization = action.payload.organization;
        state.role = action.payload.role;
        state.sessionId = action.payload.session.id;
        state.sessionExpiresAt = action.payload.session.expiresAt;
        state.lastActivity = new Date().toISOString();
        state.error = null;
        state.otpError = null;
      })
      .addCase(verifyOtpAndLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpError = action.payload; // Also set OTP error for the component to display
      })
      // Session restoration cases
      .addCase(restoreSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.isAuthorized = true;
        state.isProfileLoaded = true;
        state.isSessionRestored = true;
        state.user = action.payload.user;
        state.permissions = action.payload.permissions;
        state.organization = action.payload.organization;
        state.role = action.payload.role;
        state.sessionId = action.payload.sessionId;
        state.sessionExpiresAt = action.payload.sessionExpiresAt;
        state.lastActivity = action.payload.lastActivity;
        state.error = null;
        // Skip OTP verification since session is restored
        state.otpVerificationRequired = false;
        state.otpVerificationCompleted = true;
        state.otpError = null;
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isSessionRestored = false;
        state.error = action.payload;
        // Clear any invalid session data
        state.sessionId = null;
        state.sessionExpiresAt = null;
        state.lastActivity = null;
      })
      .addCase(requestOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.otpError = null;
      })
      .addCase(requestOtp.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpError = action.payload;
      });
  },
});

export const {
  setAuthReady,
  clearAuthError,
  // onAuthStateChanged, // Removed
  setOTPVerificationRequired,
  setOTPVerificationCompleted,
  setOTPError,
  clearOTPError,
  // Temporary access actions
  setTempAccessToken,
  updateTempAccessStatus,
  setTempAccessMonitoring,
  clearTempAccess,
  loginSuccess,
  // Session management actions
  setAuthLoadingPhase,
  setSessionData,
  updateLastActivity,
  clearSession,
  setSessionRestored
} = authSlice.actions;

export default authSlice.reducer;
